import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';

import Logo from '@/components/brand/Logo';
import { useAuth } from '@/features/auth';
import { AdminAvatar } from '../ui/AdminAvatar';
import { AdminIconButton } from '../ui/AdminIconButton';
import { AdminMenu } from '../ui/AdminMenu';
import { ADMIN_TYPE } from '../../constants/theme';
import { mainMenu, dataMenu, platformMenu } from '../../constants/navigation';
import { roleKey } from '../../utils/roleUtils';
import type { AdminSidebarProps, MenuEntry } from '../../types';
import { NavRow, NavSection } from './NavSection';

const Group: React.FC<{ label: string; items: MenuEntry[]; open: boolean; onItemClick: () => void; first?: boolean }> = ({
  label, items, open, onItemClick, first,
}) => (
  <>
    {open
      ? <Box sx={{ ...ADMIN_TYPE.groupLabel, color: 'text.disabled', pl: '10px', pt: first ? '6px' : '18px', pb: '6px' }}>{label}</Box>
      : !first && <Box sx={{ height: '1px', width: 24, bgcolor: 'divider', mx: 'auto', my: '10px' }} />}
    <NavSection items={items} sidebarOpen={open} onItemClick={onItemClick} />
  </>
);

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ sidebarOpen, setSidebarOpen, onMobileClose, user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const isSuperuser = !!user?.is_superuser;
  const platformItems = platformMenu.filter((m) => !m.superuser || isSuperuser);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const displayName = fullName || user?.username || t('admin.sidebar.fallbackUsername');
  const role = t(roleKey(user));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', height: 60, flexShrink: 0, pl: sidebarOpen ? '18px' : 0, pr: sidebarOpen ? '10px' : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center' }}>
        {sidebarOpen ? (
          <>
            <Logo width={24} height={19} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
              <Box component="span" sx={{ ...ADMIN_TYPE.cardTitle, color: 'text.primary' }}>{t('admin.brandSuffix')}</Box>
              <Box component="span" sx={{ ...ADMIN_TYPE.caption, color: 'text.secondary', bgcolor: 'action.selected', borderRadius: '5px', px: '6px', py: '1px' }}>{t('admin.brandPrefix')}</Box>
            </Box>
            <AdminIconButton icon="sidebar" title={t('admin.sidebar.collapse')} onClick={() => setSidebarOpen((o) => !o)} />
          </>
        ) : (
          <Tooltip title={t('admin.sidebar.expand')} placement="right">
            <ButtonBase onClick={() => setSidebarOpen((o) => !o)} sx={{ borderRadius: '8px', p: '6px' }}>
              <Logo width={24} height={19} />
            </ButtonBase>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: '12px', pt: '4px' }}>
        <Group first label={t('admin.sidebar.groups.main')} items={mainMenu} open={sidebarOpen} onItemClick={onMobileClose} />
        <Group label={t('admin.sidebar.groups.data')} items={dataMenu} open={sidebarOpen} onItemClick={onMobileClose} />
        <Group label={t('admin.sidebar.groups.platform')} items={platformItems} open={sidebarOpen} onItemClick={onMobileClose} />
      </Box>

      <Box sx={{ px: '12px', pt: '8px', pb: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Tooltip title={sidebarOpen ? '' : t('admin.sidebar.dashboard')} placement="right">
          <ButtonBase onClick={() => navigate('/dashboard')} sx={{ display: 'block', textAlign: 'left', borderRadius: '8px' }}>
            <NavRow icon="arrowBack" label={t('admin.sidebar.dashboard')} open={sidebarOpen} />
          </ButtonBase>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', pt: '8px', pb: '8px', pl: sidebarOpen ? '10px' : 0, pr: sidebarOpen ? '4px' : 0, justifyContent: sidebarOpen ? 'flex-start' : 'center', borderTop: '1px solid', borderColor: 'divider' }}>
          <Tooltip title={sidebarOpen ? '' : `${displayName} — ${role}`} placement="right">
            <Box><AdminAvatar name={displayName} size={32} /></Box>
          </Tooltip>
          {sidebarOpen && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ ...ADMIN_TYPE.strong, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</Box>
                <Box sx={{ ...ADMIN_TYPE.caption, color: 'text.secondary' }}>{role}</Box>
              </Box>
              <AdminIconButton icon="more" onClick={(e) => setMenuAnchor(e.currentTarget)} />
              <AdminMenu
                anchorEl={menuAnchor}
                onClose={() => setMenuAnchor(null)}
                items={[
                  { key: 'app', label: t('admin.sidebar.dashboard'), icon: 'arrowBack', onClick: () => navigate('/dashboard') },
                  { key: 'd', divider: true },
                  { key: 'logout', label: t('admin.sidebar.logout'), icon: 'logout', onClick: () => { logout(); } },
                ]}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
